import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  Icon,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { format as formatFns } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import EditarContaAPagar from "layouts/payables/components/editData";
import { findBillingStatus } from "layouts/payables/components/yupSchema";
import EditarContaAReceber from "layouts/receivables/components/editData";
import { useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { exportToExcel } from "react-json-to-excel";
import ContasService from "services/accounts";
import EmpresasService from "services/empresas";
import ReportsService from "services/reports";
import { ptBR_DataGrid } from "utils/datagrid";
import { dataCalendario } from "utils/dataCalendario";

registerLocale("ptBR", ptBR);

const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number(v) || 0
  );

const ORIGEM = { lote: "Lote de baixa", conta: "Pagamento da conta" };

// Linha da tabela a partir da conta do extrato (pagar = "S", receber = "E").
const linha = (c, tipo) => ({
  id: c.id,
  contaId: c._id,
  tipo,
  data: dataCalendario(
    c.origemMovimento === "previsto" ? c.dataVencimento : c.dataPagamento
  ),
  pessoa:
    c.supplier?.razaoSocial ||
    c.supplier?.nomeFantasia ||
    c.customer?.razaoSocial ||
    c.customer?.nomeFantasia ||
    "",
  descricao: c.descricao || "",
  centroCusto: c.accountGroup?.descricao || "",
  natureza: c.accountSubGroup?.nome || "",
  banco: c.account?.nome || "",
  origem: ORIGEM[c.origemMovimento] || "",
  status: findBillingStatus(c.status),
  valor: c.valor,
});

const separar = (contas = []) => contas.map((c) => linha(c, c.tipoMovimento));

function Resumo({ titulo, valor, detalhe, destaque = false }) {
  return (
    <Card sx={{ p: 2, height: "100%", boxShadow: destaque ? 3 : 1 }}>
      <Typography variant="caption" color="text.secondary">
        {titulo}
      </Typography>
      <Typography
        variant="h5"
        sx={{ color: Number(valor) < 0 ? "error.dark" : "text.primary" }}
      >
        {brl(valor)}
      </Typography>
      {detalhe && (
        <Typography variant="caption" color="text.secondary">
          {detalhe}
        </Typography>
      )}
    </Card>
  );
}

/**
 * Fluxo de caixa: realizado (o que entrou e saiu de fato: baixas de lote e
 * contas pagas) e projetado (contas em aberto pelo vencimento, só o que falta)
 * mostrados em separado. Antes ("Saldo entre Datas") os dois se misturavam.
 */
export default function SaldoEntreDatas() {
  const hoje = new Date();
  const [inicio, setInicio] = useState(
    new Date(hoje.getFullYear(), hoje.getMonth(), 1)
  );
  const [fim, setFim] = useState(
    new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)
  );
  const [empresa, setEmpresa] = useState("");
  const [account, setAccount] = useState("");
  const [tipo, setTipo] = useState("");
  const [empresas, setEmpresas] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [editarPagar, setEditarPagar] = useState(null);
  const [editarReceber, setEditarReceber] = useState(null);

  const buscar = async () => {
    setLoading(true);
    setErro("");
    try {
      setDados(
        await ReportsService.fluxoCaixa({
          dataInicio: formatFns(inicio, "yyyy-MM-dd"),
          dataFim: formatFns(fim, "yyyy-MM-dd"),
          empresa,
          account,
        })
      );
    } catch (e) {
      setErro(e?.error || e?.message || "Erro ao calcular o fluxo de caixa.");
    }
    setLoading(false);
  };

  useEffect(() => {
    EmpresasService.list()
      .then(({ data }) => setEmpresas(data || []))
      .catch(() => {});
    ContasService.list()
      .then(({ data }) => setContasBancarias(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (inicio && fim) buscar();
  }, [inicio, fim, empresa, account]);

  const filtrar = (linhas) =>
    tipo ? linhas.filter((l) => l.tipo === tipo) : linhas;
  const realizado = filtrar(separar(dados?.realizado?.movimentos));
  const projetado = filtrar(separar(dados?.projetado?.contas));
  const vencido = filtrar(separar(dados?.vencidoEmAberto?.contas));

  const editar = (row) =>
    row.tipo === "S"
      ? setEditarPagar(row.contaId)
      : setEditarReceber(row.contaId);

  const colunas = (comOrigem) => [
    {
      field: "data",
      headerName: comOrigem ? "Data" : "Vencimento",
      minWidth: 105,
      flex: 0.7,
    },
    { field: "pessoa", headerName: "Cliente / Fornecedor", flex: 1.2 },
    { field: "descricao", headerName: "Descrição", flex: 1.2 },
    { field: "centroCusto", headerName: "Centro de Custo", flex: 0.9 },
    { field: "natureza", headerName: "Natureza", flex: 0.9 },
    { field: "banco", headerName: "Conta bancária", minWidth: 130, flex: 0.8 },
    comOrigem
      ? { field: "origem", headerName: "Origem", minWidth: 150, flex: 0.9 }
      : { field: "status", headerName: "Status", flex: 0.6 },
    {
      field: "valor",
      headerName: comOrigem ? "Valor" : "Falta",
      flex: 0.8,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          sx={{
            color: row.tipo === "S" ? "error.dark" : "success.dark",
            fontWeight: 500,
          }}
        >
          {row.tipo === "S" ? "- " : "+ "}
          {brl(row.valor)}
        </Typography>
      ),
    },
    {
      field: "acoes",
      headerName: "",
      flex: 0.3,
      sortable: false,
      renderCell: ({ row }) => (
        <MDButton
          variant="gradient"
          color="info"
          iconOnly
          onClick={() => editar(row)}
        >
          <Icon fontSize="small">edit</Icon>
        </MDButton>
      ),
    },
  ];

  const tabela = (titulo, linhas, comOrigem, totaisSecao) => (
    <Box mt={4}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        mb={1}
      >
        <Typography variant="h6">{titulo}</Typography>
        {totaisSecao && (
          <Typography variant="body2" color="text.secondary">
            Entradas {brl(totaisSecao.entradas)} · Saídas{" "}
            {brl(totaisSecao.saidas)} · Saldo <b>{brl(totaisSecao.saldo)}</b>
          </Typography>
        )}
      </Box>
      <DataGrid
        rows={linhas}
        columns={colunas(comOrigem)}
        autoHeight
        density="compact"
        loading={loading}
        localeText={ptBR_DataGrid}
        rowSelection={false}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50, 100]}
      />
    </Box>
  );

  const exportar = () => {
    const planilha = (linhas) =>
      linhas.map((l) => ({
        Data: l.data,
        "Cliente/Fornecedor": l.pessoa,
        Descrição: l.descricao,
        "Centro de Custo": l.centroCusto,
        Natureza: l.natureza,
        "Conta bancária": l.banco,
        Origem: l.origem,
        Status: l.status,
        Tipo: l.tipo === "S" ? "Saída" : "Entrada",
        Valor: (l.tipo === "S" ? -1 : 1) * l.valor,
      }));
    exportToExcel(
      [
        { sheetName: "Realizado", details: planilha(realizado) },
        { sheetName: "Projetado", details: planilha(projetado) },
        { sheetName: "Vencido em aberto", details: planilha(vencido) },
      ],
      `fluxo_caixa_${formatFns(inicio, "yyyy-MM-dd")}_${formatFns(
        fim,
        "yyyy-MM-dd"
      )}`,
      true
    );
  };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        {editarPagar && (
          <EditarContaAPagar
            setShowEditState={() => setEditarPagar(null)}
            currentIdAction={editarPagar}
            fetchData={buscar}
          />
        )}
        {editarReceber && (
          <EditarContaAReceber
            setShowEditState={() => setEditarReceber(null)}
            currentIdAction={editarReceber}
            fetchData={buscar}
          />
        )}

        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              Fluxo de Caixa: realizado x projetado
            </MDTypography>
          </MDBox>

          <MDBox p={3}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <DatePicker
                selected={inicio}
                onChange={(d) => d && setInicio(d)}
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                customInput={<TextField label="De" size="small" />}
              />
              <DatePicker
                selected={fim}
                onChange={(d) => d && setFim(d)}
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
                customInput={<TextField label="Até" size="small" />}
              />
              <TextField
                label="Empresa"
                size="small"
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                value={empresa}
                onChange={(e) => setEmpresa(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <option value="">Todas</option>
                {empresas.map((e) => (
                  <option key={e._id} value={e._id}>
                    {e.nomeFantasia || e.razaoSocial}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Conta bancária"
                size="small"
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                <option value="">Todas</option>
                {contasBancarias
                  .filter(
                    (c) => !empresa || (c.empresa?._id || c.empresa) === empresa
                  )
                  .map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.nome}
                    </option>
                  ))}
              </TextField>
              <TextField
                label="Tipo"
                size="small"
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
              >
                <option value="">Entradas e saídas</option>
                <option value="E">Só entradas</option>
                <option value="S">Só saídas</option>
              </TextField>
              <Box flexGrow={1} />
              <Button
                variant="contained"
                sx={{ color: "white !important" }}
                onClick={exportar}
                disabled={!dados}
              >
                Exportar para Excel
              </Button>
            </Box>

            {(empresa || account) && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
              >
                Com filtro de empresa ou conta bancária, entram só as contas e
                baixas que têm conta bancária informada.
              </Typography>
            )}

            {erro && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {erro}
              </Alert>
            )}

            {dados && (
              <Grid container spacing={2} mt={1}>
                <Grid item xs={6} md={2}>
                  <Resumo
                    titulo="Saldo anterior (realizado)"
                    valor={dados.saldoAnterior}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <Resumo
                    titulo="Realizado no período"
                    valor={dados.realizado.saldo}
                    detalhe={`+${brl(dados.realizado.entradas)} / -${brl(
                      dados.realizado.saidas
                    )}`}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <Resumo
                    titulo="Saldo realizado"
                    valor={dados.saldoFinalRealizado}
                    destaque
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <Resumo
                    titulo="Projetado no período"
                    valor={dados.projetado.saldo}
                    detalhe={`+${brl(dados.projetado.entradas)} / -${brl(
                      dados.projetado.saidas
                    )}`}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <Resumo
                    titulo="Vencido em aberto"
                    valor={dados.vencidoEmAberto.saldo}
                    detalhe="antes do período"
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <Resumo
                    titulo="Saldo projetado"
                    valor={dados.saldoFinalProjetado}
                    destaque
                  />
                </Grid>
              </Grid>
            )}

            {tabela("Realizado", realizado, true, dados?.realizado)}
            {tabela(
              "Projetado (vence no período)",
              projetado,
              false,
              dados?.projetado
            )}
            {vencido.length > 0 &&
              tabela(
                "Vencido em aberto (antes do período)",
                vencido,
                false,
                dados?.vencidoEmAberto
              )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
